import { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, X, FileText, ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TipoGasto, CategoriaGasto } from '@/components/hooks/useExpenses';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  expenseToEdit?: any | null;
  tiposGasto: TipoGasto[];
  categoriasGasto: CategoriaGasto[];
  crearGasto: (payload: any) => Promise<any>;
  editarGasto: (id: string, payload: any) => Promise<any>;
}

interface ExpenseForm {
  concepto: string;
  base_imponible: string;
  iva_porcentaje: string;
  fecha: string;
  tipo: string;
  categoria: string;
}

// Duración mínima del efecto de escaneo (ms) para que se vea completo
const SCAN_MIN_MS = 2600;

// ─── TopBar con pasos ────────────────────────────────────────────────────────
const DialogTopBar = ({
  step,
  onClose,
}: {
  step: 1 | 2;
  onClose: () => void;
}) => (
  <div className="flex items-center gap-3 px-4 pt-3 pb-2">
    <div className="flex flex-1 gap-1.5">
      <div className="h-1 flex-1 rounded-full bg-[#103254] dark:bg-blue-400" />
      <div
        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
          step === 2
            ? 'bg-[#103254] dark:bg-blue-400'
            : 'bg-gray-200 dark:bg-white/10'
        }`}
      />
    </div>
    <button
      onClick={onClose}
      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      aria-label="Cerrar"
    >
      <X className="w-5 h-5" />
    </button>
  </div>
);

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
      {label}
    </label>
    {children}
  </div>
);

// ─── Resumen card para paso 2 ─────────────────────────────────────────────────
const ResumenCard = ({
  concepto,
  tipo,
  categoria,
  base,
  iva,
  fecha,
}: {
  concepto: string;
  tipo: string;
  categoria: string;
  base: string;
  iva: string;
  fecha: string;
}) => {
  const total = (parseFloat(base) || 0) * (1 + (parseFloat(iva) || 0) / 100);

  return (
    <div className="rounded-xl border border-[#103254]/20 dark:border-blue-400/20 bg-[#103254]/5 dark:bg-blue-400/5 p-3 space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-[#103254] dark:bg-blue-500 flex items-center justify-center shrink-0">
          <FileText className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#103254] dark:text-blue-300 truncate">
            {concepto}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {tipo}
            {categoria ? ` · ${categoria}` : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-semibold text-[#103254] dark:text-blue-300">
            {isNaN(total) ? '0.00' : total.toFixed(2)} €
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {fecha}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Overlay de escaneo ───────────────────────────────────────────────────────
const ScanOverlay = () => (
  <div className="absolute inset-0 pointer-events-none select-none">
    <div
      className="absolute inset-0 bg-[#0a2540]/35"
      style={{
        backgroundImage:
          'linear-gradient(rgba(38,212,176,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(38,212,176,0.10) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    />

    {[
      'top-2 left-2 border-t-2 border-l-2 rounded-tl',
      'top-2 right-2 border-t-2 border-r-2 rounded-tr',
      'bottom-2 left-2 border-b-2 border-l-2 rounded-bl',
      'bottom-2 right-2 border-b-2 border-r-2 rounded-br',
    ].map((pos) => (
      <span
        key={pos}
        className={`absolute w-6 h-6 border-[#26d4b0] ${pos}`}
      />
    ))}

    <div className="expense-scan-band absolute left-0 right-0 h-16">
      <div className="absolute inset-x-0 top-1/2 h-px bg-[#26d4b0] shadow-[0_0_14px_3px_rgba(38,212,176,0.85)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#26d4b0]/25 to-transparent" />
    </div>

    <div className="absolute inset-x-0 bottom-3 flex justify-center">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#0a2540]/85 px-3 py-1.5 rounded-full backdrop-blur-sm border border-[#26d4b0]/40">
        <ScanLine className="w-3.5 h-3.5 text-[#26d4b0] animate-pulse" />
        Digitalizando recibo · creando PDF
      </span>
    </div>
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
export const CreateExpenseDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSaved,
  expenseToEdit,
  tiposGasto,
  categoriasGasto,
  crearGasto,
  editarGasto,
}) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeReceipt, setRemoveReceipt] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<ExpenseForm>({
    concepto: '',
    base_imponible: '',
    iva_porcentaje: '21',
    fecha: new Date().toISOString().split('T')[0],
    tipo: '',
    categoria: '',
  });

  const handleNumericChange = (field: keyof ExpenseForm, value: string) => {
    const formatted = value.replace(',', '.');
    if (/^\d*\.?\d*$/.test(formatted)) {
      setForm((p) => ({ ...p, [field]: formatted }));
    }
  };

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!open) return;

    setStep(1);
    setRemoveReceipt(false);
    setScanning(false);

    if (!expenseToEdit) {
      setForm({
        concepto: '',
        base_imponible: '',
        iva_porcentaje: '21',
        fecha: new Date().toISOString().split('T')[0],
        tipo: '',
        categoria: '',
      });
      setFile(null);
      setPreviewUrl(null);
    } else {
      setForm({
        concepto: expenseToEdit.concepto ?? '',
        base_imponible: String(
          expenseToEdit.base_imponible ?? expenseToEdit.recibos?.[0]?.base ?? ''
        ),
        iva_porcentaje: String(expenseToEdit.iva_porcentaje ?? '21'),
        fecha:
          expenseToEdit.fecha_inicio ?? new Date().toISOString().split('T')[0],
        tipo: expenseToEdit.id_tipo_gasto ?? '',
        categoria: expenseToEdit.id_categoria_gasto ?? '',
      });
      setFile(null);
      setPreviewUrl(
        expenseToEdit.url_recibo ?? expenseToEdit.recibos?.[0]?.url ?? null
      );
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [open, expenseToEdit]);

  const handleClose = () => {
    if (loading) return;
    setStep(1);
    onOpenChange(false);
  };

  const step1Valid =
    form.concepto.trim() &&
    form.tipo &&
    form.categoria &&
    form.fecha &&
    form.base_imponible;

  const tipoNombre =
    tiposGasto.find((t) => t.id_tipo_gasto === form.tipo)?.nombre ?? '';
  const categoriaNombre =
    categoriasGasto.find((c) => c.id_categoria_gasto === form.categoria)
      ?.nombre ?? '';

  const handleSave = async () => {
    const esImagen = !!file && file.type.startsWith('image/');
    const inicio = Date.now();

    setLoading(true);
    if (esImagen) setScanning(true);

    try {
      const payload = {
        concepto: form.concepto,
        fecha: form.fecha,
        base_imponible: parseFloat(form.base_imponible) || 0,
        iva_porcentaje: parseFloat(form.iva_porcentaje) || 0,
        archivo: file,
        removeReceipt,
        id_tipo_gasto: form.tipo,
        id_categoria_gasto: form.categoria,
      };

      const { error } = expenseToEdit
        ? await editarGasto(expenseToEdit.id_gasto, payload)
        : await crearGasto(payload);

      if (error) throw error;

      if (esImagen) {
        const transcurrido = Date.now() - inicio;
        if (transcurrido < SCAN_MIN_MS) {
          await new Promise((r) => setTimeout(r, SCAN_MIN_MS - transcurrido));
        }
      }

      toast.success(
        expenseToEdit
          ? t('createExpenseDialog.successEdit')
          : t('createExpenseDialog.successNew')
      );
      onSaved();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(t('createExpenseDialog.errorSave'));
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const isPdfPreview =
    !!previewUrl &&
    ((file && file.type === 'application/pdf') ||
      previewUrl.toLowerCase().includes('.pdf'));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[94vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-xl sm:mx-auto sm:h-auto sm:max-h-[94vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >        
      <style>{`
          @keyframes expenseScanSweep {
            0%   { top: -4rem; opacity: 0; }
            12%  { opacity: 1; }
            88%  { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }
          .expense-scan-band {
            animation: expenseScanSweep 2.2s cubic-bezier(0.45, 0, 0.55, 1) infinite;
            will-change: top;
          }
          @media (prefers-reduced-motion: reduce) {
            .expense-scan-band { animation-duration: 0.01ms; animation-iteration-count: 1; opacity: 0; }
          }
        `}</style>

        <DialogTopBar step={step} onClose={handleClose} />

        <DialogHeader className="px-5 pb-1">
          <DialogTitle className="text-sm font-semibold text-[#103254] dark:text-blue-400">
            {expenseToEdit
              ? t('createExpenseDialog.titleEdit')
              : t('createExpenseDialog.titleNew')}
          </DialogTitle>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {step === 1
              ? t(
                  'createExpenseDialog.step1Label',
                  'Paso 1 de 2 — Datos del gasto'
                )
              : t('createExpenseDialog.step2Label', 'Paso 2 de 2 — Recibo')}
          </p>
        </DialogHeader>

        <div className="px-5 py-3 space-y-3 text-sm text-gray-700 dark:text-gray-300 flex-1 overflow-y-auto">
          {/* ══ PASO 1 ══════════════════════════════════════════════════════ */}
          {step === 1 && (
            <>
              <Field label={t('createExpenseDialog.labelConcept')}>
                <input
                  value={form.concepto}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, concepto: e.target.value }))
                  }
                  placeholder={t('createExpenseDialog.placeholderConcept')}
                  className="flex h-10 w-full rounded-md border border-input dark:border-white/10 bg-background dark:bg-white/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:text-white"
                />
              </Field>

              <Field label={t('createExpenseDialog.labelType')}>
                <Select
                  value={form.tipo}
                  onValueChange={(v) => setForm((p) => ({ ...p, tipo: v }))}
                >
                  <SelectTrigger className="h-10 text-sm dark:bg-white/5 dark:border-white/10 dark:text-white">
                    <SelectValue
                      placeholder={t('createExpenseDialog.placeholderType')}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {tiposGasto.map((tp) => (
                      <SelectItem
                        key={tp.id_tipo_gasto}
                        value={tp.id_tipo_gasto}
                        className="text-sm"
                      >
                        {tp.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t('createExpenseDialog.labelCategory')}>
                <Select
                  value={form.categoria}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, categoria: v }))
                  }
                >
                  <SelectTrigger className="h-10 text-sm dark:bg-white/5 dark:border-white/10 dark:text-white">
                    <SelectValue
                      placeholder={t('createExpenseDialog.placeholderCategory')}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {categoriasGasto.map((c) => (
                      <SelectItem
                        key={c.id_categoria_gasto}
                        value={c.id_categoria_gasto}
                        className="text-sm"
                      >
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t('createExpenseDialog.labelBase')}>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={form.base_imponible}
                    onChange={(e) =>
                      handleNumericChange('base_imponible', e.target.value)
                    }
                    className="flex h-10 w-full rounded-md border border-input dark:border-white/10 bg-background dark:bg-white/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:text-white"
                  />
                </Field>

                <Field label={t('createExpenseDialog.labelIva')}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.iva_porcentaje}
                    onChange={(e) =>
                      handleNumericChange('iva_porcentaje', e.target.value)
                    }
                    className="flex h-10 w-full rounded-md border border-input dark:border-white/10 bg-background dark:bg-white/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:text-white"
                  />
                </Field>
              </div>

              <Field label={t('createExpenseDialog.labelDate')}>
                <Input
                  type="date"
                  value={form.fecha}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, fecha: e.target.value }))
                  }
                  className="h-10 text-sm min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>
            </>
          )}

          {/* ══ PASO 2 ══════════════════════════════════════════════════════ */}
          {step === 2 && (
            <>
              <ResumenCard
                concepto={form.concepto}
                tipo={tipoNombre}
                categoria={categoriaNombre}
                base={form.base_imponible}
                iva={form.iva_porcentaje}
                fecha={form.fecha}
              />

              <div
                className={`relative border-2 border-dashed rounded-xl transition-all mt-2 ${
                  previewUrl
                    ? 'border-[#103254] bg-gray-50 dark:bg-white/5'
                    : 'border-gray-300 dark:border-white/10 hover:border-[#103254] cursor-pointer p-8'
                }`}
                onClick={() =>
                  !previewUrl && !loading && fileInputRef.current?.click()
                }
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    if (f) setRemoveReceipt(false);
                  }}
                  accept=".pdf,image/*"
                />

                {previewUrl ? (
                  <div className="relative w-full flex justify-center p-2">
                    {isPdfPreview ? (
                      <iframe
                        src={previewUrl}
                        title="Vista previa del recibo en PDF"
                        className="w-full h-[400px] rounded shadow-md bg-white"
                      />
                    ) : (
                      <div className="relative inline-block overflow-hidden rounded shadow-md leading-[0]">
                        <img
                          src={previewUrl}
                          alt="Vista previa del recibo"
                          className="max-w-full h-auto max-h-[400px] object-contain"
                        />
                        {scanning && <ScanOverlay />}
                      </div>
                    )}

                    {!loading && (
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-4 right-4 rounded-full h-8 w-8 shadow-xl z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          setPreviewUrl(null);
                          setRemoveReceipt(true);
                          if (fileInputRef.current)
                            fileInputRef.current.value = '';
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {t('createExpenseDialog.uploadText')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('createExpenseDialog.uploadHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {t(
                  'createExpenseDialog.receiptOptional',
                  'El recibo es opcional, puedes guardarlo sin adjuntar uno. Las fotos se guardan como PDF automáticamente.'
                )}
              </p>
            </>
          )}
        </div>

        {/* ── FOOTER ──────────────────────────────────────────────────────── */}
        <DialogFooter className="px-5 pt-2 pb-5 sm:pb-4 flex flex-row gap-2 border-t border-gray-100 dark:border-white/10">
          {step === 2 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              disabled={loading}
              className="flex-1 h-9 rounded-lg border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 text-sm disabled:opacity-50"
            >
              {t('createExpenseDialog.back', 'Atrás')}
            </Button>
          )}
          {step === 1 ? (
            <Button
              type="button"
              onClick={() => setStep(2)}
              disabled={!step1Valid}
              className="w-full h-9 rounded-lg bg-[#103254] hover:bg-[#0d2742] text-white text-sm font-semibold disabled:opacity-50"
            >
              {t('createExpenseDialog.next', 'Siguiente')}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="flex-1 h-9 rounded-lg bg-[#103254] hover:bg-[#0d2742] text-white text-sm font-semibold disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <ScanLine className="w-4 h-4 animate-pulse" />
                  {t('createExpenseDialog.scanning', 'Digitalizando…')}
                </span>
              ) : expenseToEdit ? (
                t('createExpenseDialog.saveChanges')
              ) : (
                t('createExpenseDialog.createExpense')
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};